// Country code (ISO-3166-1 alpha-2, used as TMDB watch_region) ->
// primary content language(s) (TMDB with_original_language values,
// ISO-639-1). English is always appended as a secondary so Anglophone
// blockbusters still surface for users in non-English regions.
//
// This list is intentionally curated rather than exhaustive -- the
// fallback for unknown regions is "en|fr" (the legacy hardcode) so
// existing behavior is preserved for users who haven't picked a region
// the table knows about.

const REGION_TO_LANGUAGES = Object.freeze({
  // English-primary regions
  US: ['en'],
  GB: ['en'],
  CA: ['en'],
  AU: ['en'],
  NZ: ['en'],
  IE: ['en'],
  ZA: ['en'],

  // Western Europe
  FR: ['fr', 'en'],
  DE: ['de', 'en'],
  ES: ['es', 'en'],
  IT: ['it', 'en'],
  PT: ['pt', 'en'],
  NL: ['nl', 'en'],
  BE: ['fr', 'nl', 'en'],
  CH: ['de', 'fr', 'it', 'en'],
  AT: ['de', 'en'],
  GR: ['el', 'en'],

  // Nordics
  SE: ['sv', 'en'],
  NO: ['no', 'en'],
  DK: ['da', 'en'],
  FI: ['fi', 'en'],
  IS: ['is', 'en'],

  // Eastern Europe
  PL: ['pl', 'en'],
  CZ: ['cs', 'en'],
  HU: ['hu', 'en'],
  RO: ['ro', 'en'],
  BG: ['bg', 'en'],
  UA: ['uk', 'ru', 'en'],
  RU: ['ru', 'en'],

  // Asia
  JP: ['ja', 'en'],
  KR: ['ko', 'en'],
  CN: ['zh', 'en'],
  TW: ['zh', 'en'],
  HK: ['zh', 'en'],
  SG: ['en', 'zh', 'ms'],
  TH: ['th', 'en'],
  VN: ['vi', 'en'],
  ID: ['id', 'en'],
  MY: ['ms', 'en'],
  PH: ['en', 'tl'],
  IN: ['hi', 'en'],
  PK: ['ur', 'en'],
  BD: ['bn', 'en'],
  LK: ['si', 'ta', 'en'],

  // Middle East
  IL: ['he', 'en'],
  TR: ['tr', 'en'],
  AE: ['ar', 'en'],
  SA: ['ar', 'en'],
  EG: ['ar', 'en'],
  JO: ['ar', 'en'],
  LB: ['ar', 'en'],
  MA: ['ar', 'fr', 'en'],
  TN: ['ar', 'fr', 'en'],

  // Americas
  MX: ['es', 'en'],
  BR: ['pt', 'en'],
  AR: ['es', 'en'],
  CL: ['es', 'en'],
  CO: ['es', 'en'],
  PE: ['es', 'en'],
  VE: ['es', 'en'],
});

// Fallback when the user's region isn't in the table or they're using
// a "pre-region-picker" install. Matches the legacy hardcode so nothing
// regresses for the existing user base.
export const DEFAULT_ORIGINAL_LANGUAGES = ['en', 'fr'];

/**
 * Return the original-language allowlist for a given watch region.
 * Always English-inclusive (so big-budget English content still
 * surfaces in non-English regions).
 *
 * @param {string} region — TMDB watch_region (ISO-3166 alpha-2). Case-insensitive.
 * @returns {string[]} ISO-639-1 codes, English always last.
 */
export function languagesForRegion(region) {
  if (typeof region !== 'string' || region.length === 0) {
    return [...DEFAULT_ORIGINAL_LANGUAGES];
  }
  const codes = REGION_TO_LANGUAGES[region.toUpperCase()];
  return codes ? [...codes] : [...DEFAULT_ORIGINAL_LANGUAGES];
}

/**
 * Convenience: pipe-joined string in the shape TMDB expects for
 * `with_original_language` (e.g. "th|en"). All existing call sites
 * pass that exact format, so the helper hands them a drop-in value.
 */
export function originalLanguagePipe(region) {
  return languagesForRegion(region).join('|');
}
